import { useCallback, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import { Modal } from '@components/common';
import {
  Button, RecordInterface, SetFormValues, GlobalValuesTypes,
} from '@components/common/Forms';
import { CategorisedStaticBenefits } from '@type/job-add';
import { EmployerBenefitsList } from '@components/templates/job-add/edit/form/partials/EmployerBenefitsList';
import { dataTestId } from '@services/data-test-id';

/** Props */
interface EditBenefitsModalProps {
  record: RecordInterface,
  setFormValues: SetFormValues,
  getFieldValue: (name: string) => GlobalValuesTypes,
  benefits?: CategorisedStaticBenefits[],
  selectedBenefits?: number[],
  setSelectedBenefits?: Dispatch<SetStateAction<number[] | undefined>>,
  updateCurrentProfileBenefits?: (newBenefits?: number[]) => void,
  isOpen: boolean,
  toggleModal: () => void,
}

/**
 * @description Edit Benefits modal component.
 * - `record` - record function from useBjForm
 * - `setFormValues` - set form values function from useBjForm
 * - `getFieldValue` - get field value function from useBjForm
 * - `benefits` - The adjusted benefits list.
 * - `selectedBenefits` - The selected benefits.
 * - `setSelectedBenefits` - The set selected benefits function.
 * - `updateCurrentProfileBenefits` - The update current profile benefits function.
 * - `isOpen` - boolean, the modal state
 * - `toggleModal` - function, the modal toggle function
 */
export const EditBenefitsModal = (props: EditBenefitsModalProps) => {
  // Destructure props
  const {
    record,
    setFormValues,
    getFieldValue,
    benefits,
    selectedBenefits,
    setSelectedBenefits,
    updateCurrentProfileBenefits,
    isOpen,
    toggleModal,
  } = props;

  // Translation
  const { t } = useTranslation();
  const inputName = 'benefitsEdit';

  // Save selected benefits
  const saveSelectedBenefits = useCallback(() => {
    const selectedIds = getFieldValue(inputName) as number[];
    setSelectedBenefits?.(selectedIds);
    updateCurrentProfileBenefits?.(selectedIds);
    toggleModal();
  }, [getFieldValue, setSelectedBenefits, toggleModal, updateCurrentProfileBenefits]);

  // Render component
  // *************************
  return (
    <Modal
      isOpen={isOpen}
      onClose={toggleModal}
      size="lg"
      header={t('benefits.choose.modal.title')}
      beforeEnter={() => setFormValues({ benefitsEdit: selectedBenefits })}
    >
      <Modal.Body
        className="bg-surface-50"
      >
        <form noValidate>
          <EmployerBenefitsList
            record={record}
            benefits={benefits}
            inputName={inputName}
            type="form"
          />
        </form>
      </Modal.Body>

      <Modal.Footer
        className="text-right"
      >
        <Button
          size="sm"
          color="secondary"
          onClick={saveSelectedBenefits}
          {...dataTestId('jobAddPage.jobBenefits.saveBenefitsButton')}
        >
          {t('global.action.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
